import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SortEnum } from '@sport-activities/sdk';
import { format } from 'date-fns';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { PaginationTypeEnum } from '~/attendee/components/shared/pagination/enums/pagination-type.enum';
import { EventsByDate } from '~/attendee/interfaces/event.interface';
import { PaginatedSearchService } from '~/attendee/service/paginated-search.service';
import {
  ResultPageFilters,
  ResultPageFilterService,
} from '~/attendee/service/result-page-filter.service';
import { SubscriptionSearchService } from '~/attendee/service/subscription-search.service';
import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { mergeDates } from '~/core/helpers/merge-dates/merge-dates.helper';
import { deepMerge } from '~/core/helpers/object/deep-merge/deep-merge.helper';
import { Activity } from '~/core/model/activity.model';
import { CustomHeadTagService } from '~/core/services/custom-head-tag/custom-head-tag.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { TagManagerService } from '~/core/services/tag-manager/tag-manager.service';
import { LocaleService } from '~/i18n/services/locale.service';

import { environment } from 'src/environments/environment';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { NavigatorService } from '~/attendee/service/navigator.service';
import { ActivityFilterParamsInterface } from '~/attendee/interfaces/activity-filter-params.interface';

const LIVES_LOADER_KEY = 'loadLives';
@Component({
  selector: 'app-attendee-result-page-lives',
  templateUrl: './lives.component.html',
})
export class ResultPageLivesComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  public hasLoadedActivities = new BehaviorSubject<boolean>(false);

  public readonly IconTypeEnum = VtmnPlayIconTypeEnum;
  public readonly paginationType = PaginationTypeEnum;
  public readonly tipsVariant = TipsVariantEnum;
  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public paramFilters: ResultPageFilters = {};

  public events: EventsByDate[];
  public hasLoadMoreButton: boolean;
  public isLoading = false;
  public isMobileDevice: boolean;
  public totalItems: number;
  public pagination: number;

  constructor(
    private readonly localeService: LocaleService,
    @Inject('ActivitySearchService')
    private readonly searchService: PaginatedSearchService<
      Activity,
      ActivityFilterParamsInterface
    >,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly subscriptionSearchService: SubscriptionSearchService,
    private readonly loaderService: LoaderService,
    private readonly matchMediaService: MatchMediaService,
    private readonly navigatorService: NavigatorService,
    private readonly resultPageFilterService: ResultPageFilterService,
    private readonly translateService: TranslateService,
    private readonly customHeadTag: CustomHeadTagService,
    private readonly tagManager: TagManagerService,
    private readonly viewportScroller: ViewportScroller
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.customHeadTag.destroyMicrodata();
  }

  public ngOnInit(): void {
    this.loaderService.start(LIVES_LOADER_KEY);

    if (isPlatformBrowser(this.platformId)) {
      this.navigatorService.buildUrlTree();
    }

    this.subscriptions.add(
      this.resultPageFilterService.filters$
        .pipe(
          mergeMap((paramFilters) => {
            this.loaderService.start(LIVES_LOADER_KEY);
            this.pagination = 0;

            const defaultFilters =
              this.subscriptionSearchService.getLiveFilters(environment.tenant);

            const searchedFilters: ActivityFilterParamsInterface = {
              sportGroups: paramFilters.sportGroups,
              ageRange: paramFilters.ageRange,
              timeslots: {
                startDate: paramFilters.startDate || new Date().toISOString(),
                endDate: paramFilters.endDate,
                priceRange: paramFilters.priceRange,
              },
            };

            const filters = deepMerge<ActivityFilterParamsInterface>(
              defaultFilters,
              searchedFilters
            );

            return this.matchMediaService.isMobileDevice().pipe(
              map((isMobileDevice) => ({
                filters,
                paramFilters,
                isMobileDevice,
              }))
            );
          }),
          mergeMap(({ filters, paramFilters }) => {
            const itemsPerPage = appConfig.pagination.result;

            const timeslotsFilters = deepMerge(filters, {
              timeslots: {
                pagination: {
                  from: (paramFilters?.page - 1) * itemsPerPage || 0,
                  limit: itemsPerPage,
                },
                sort: {
                  order: 'asc' as SortEnum.ASC,
                  by: 'startDate',
                },
              },
            });

            return this.searchService.search(filters).pipe(
              map((paginatedActivities) => ({
                paginatedActivities,
                timeslotsFilters,
                itemsPerPage,
                paramFilters,
              }))
            );
          }),
          mergeMap(
            ({
              paginatedActivities,
              timeslotsFilters,
              itemsPerPage,
              paramFilters,
            }) => {
              return this.subscriptionSearchService
                .getItemsForCalendarView(paginatedActivities, timeslotsFilters)
                .pipe(
                  map((events) => ({
                    events,
                    itemsPerPage,
                    paramFilters,
                  }))
                );
            }
          )
        )
        .subscribe(({ events, itemsPerPage, paramFilters }) => {
          const scrollPosition = this.viewportScroller.getScrollPosition();
          this.events = [];

          this.paramFilters = paramFilters;
          this.events = mergeDates(this.events?.concat(events?.eventsByDate));
          this.hasLoadMoreButton = events.total === itemsPerPage;

          this.loaderService.stop(LIVES_LOADER_KEY);

          this.totalItems = events.total;
          this.pagination = Math.ceil(
            events.total / appConfig.pagination.result
          );

          if (this.events?.length === 0) {
            this.tagManager.pushNoResult('Lives');
          }

          setTimeout(
            () => this.viewportScroller.scrollToPosition(scrollPosition),
            0
          );
        })
    );

    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        this.isLoading = this.loaderService.isLoading(LIVES_LOADER_KEY);
      })
    );

    this.setBreadcrumb();
  }

  public get tips(): string[] {
    return this.translateService
      .instant('attendee.result_page.activites_live_tips.text')
      .split('✓');
  }

  private setBreadcrumb(): void {
    this.customHeadTag.setBreadcrumb([
      this.customHeadTag.newBreadcrumItem(
        this.translateService.instant('seo.breadcrumb.item.live')
      ),
    ]);
  }

  public formatDate(date: string): string {
    const dateObj = new Date(date);
    // The date in EventsByDate is stocked without time,
    // the ƒ `format()` of date-fns applies timezone of the `dateLocale` on the date argument.
    // We have to add the difference between the timezone of EventsByDate date and the timeslot to avoid day substraction.
    const localeDate = new Date(
      dateObj.valueOf() + dateObj.getTimezoneOffset() * 60 * 1000
    );
    return format(localeDate, 'PPPP', {
      locale: this.localeService.dateLocale,
    });
  }

  public loadMore(): void {
    this.paramFilters.page += 1;
    this.resultPageFilterService.filters$.next(this.paramFilters);
  }

  public resetFilters(): void {
    this.resultPageFilterService.resetFilters();
  }
}
